import type { Edge, Node } from '@xyflow/react';
import type { TFunction } from 'i18next';
import { linearizeVisualChannelSteps, linearizeVisualPathNodes } from './visualChainLinearize';
import type { MailingChannel } from './mailingTypes';
import { normalizeChannelNodeData, normalizeConditionNodeData } from './mailingTypes';

/** Параметры готовности блока канала (единый список с кампании и т.п.). */
export type ChannelNodeReadinessOpts = {
  subscriberContactListIds?: readonly string[] | null;
  subscriberContactListId?: string | null;
  startScheduleDate?: string | null;
  startScheduleTime?: string | null;
  /** Цепочка с триггером по событию — без даты на каналах, нужен offset. */
  isEventChain?: boolean;
};

/** Обводка на холсте: успех / предупреждение / без акцента (старт/финиш/заметка/задержка). */
export type CanvasNodeRingStatus = 'success' | 'warning' | 'neutral';

function hasOutgoingHandle(edges: Edge[], sourceId: string, handle: string): boolean {
  return edges.some((e) => e.source === sourceId && String(e.sourceHandle ?? '') === handle);
}

/** Локальная готовность узла для подсветки на канве (контент + обязательные рёбра для ветвлений). */
export function canvasNodeRingStatus(
  node: Pick<Node, 'id' | 'type' | 'data'>,
  edges: Edge[],
  opts?: ChannelNodeReadinessOpts,
): CanvasNodeRingStatus {
  const id = node.id;
  switch (node.type) {
    case 'mailingStart':
    case 'mailingEnd':
      return 'neutral';
    case 'mailingNote':
      return 'neutral';
    case 'mailingDelay': {
      const legacyOut = edges.some((e) => {
        if (e.source !== id) return false;
        const sh = String(e.sourceHandle ?? '');
        return sh === '' || sh === 'out';
      });
      if (legacyOut) return 'neutral';
      const dualBranches =
        hasOutgoingHandle(edges, id, 'delivered') && hasOutgoingHandle(edges, id, 'not_delivered');
      return dualBranches ? 'neutral' : 'warning';
    }
    case 'mailingChannel': {
      const incoming = edges.some((e) => e.target === id);
      if (!incoming || !channelNodeDataLaunchReady(node.data, opts)) return 'warning';
      return 'success';
    }
    case 'mailingCondition': {
      const d = normalizeConditionNodeData(node.data);
      const content = Boolean(String(d.title ?? '').trim() || String(d.description ?? '').trim());
      const branches = hasOutgoingHandle(edges, id, 'yes') && hasOutgoingHandle(edges, id, 'no');
      if (!content || !branches) return 'warning';
      return 'success';
    }
    case 'mailingAbTest': {
      const branches = hasOutgoingHandle(edges, id, 'a') && hasOutgoingHandle(edges, id, 'b');
      if (!branches) return 'warning';
      return 'success';
    }
    default:
      return 'neutral';
  }
}

/** Подписи блоков с предупреждающей обводкой (для уведомления перед сохранением). */
export function collectUnreadyBlockLabels(
  nodes: Node[],
  edges: Edge[],
  opts: ChannelNodeReadinessOpts | undefined,
  t: TFunction,
): string[] {
  const labels: string[] = [];
  for (const n of nodes) {
    if (n.type === 'mailingStart' || n.type === 'mailingEnd' || n.type === 'mailingNote') continue;
    const ring = canvasNodeRingStatus(n, edges, opts);
    if (ring !== 'warning') continue;
    if (n.type === 'mailingChannel') {
      const ch = normalizeChannelNodeData(n.data).channel;
      labels.push(t(`channels.${ch}`));
    } else if (n.type === 'mailingDelay') {
      labels.push(t('flowNodes.delayCardTitle'));
    } else if (n.type === 'mailingCondition') {
      labels.push(t('flowNodes.conditionTitle'));
    } else if (n.type === 'mailingAbTest') {
      labels.push(t('flowNodes.abTitle'));
    }
  }
  return labels;
}

function recipientModeFilled(
  mode: 'list' | 'manual',
  contactListQuery: string,
  phonesManual: string,
  campaignSubscriberListId?: string | null,
): boolean {
  const camp = String(campaignSubscriberListId ?? '').trim();
  if (camp) return true;
  return mode === 'list'
    ? Boolean(String(contactListQuery || '').trim())
    : Boolean(String(phonesManual || '').trim());
}

function scheduleReadyForChannel(
  d: ReturnType<typeof normalizeChannelNodeData>,
  opts?: ChannelNodeReadinessOpts,
): boolean {
  const nonEmpty = (s: unknown) => Boolean(String(s ?? '').trim());
  const sd = String(opts?.startScheduleDate ?? '').trim();
  const st = String(opts?.startScheduleTime ?? '').trim();
  if (nonEmpty(sd) && nonEmpty(st)) return true;
  return nonEmpty(d.scheduleDate) && nonEmpty(d.scheduleTime);
}

/** Коды пропусков для подсказок в UI (ключи i18n: flowNodes.readiness.<code>). */
export type ChannelNodeLaunchIssueCode =
  | 'noIncoming'
  | 'schedule'
  | 'recipients'
  | 'emailCampaignLists'
  | 'pushTitle'
  | 'pushBody'
  | 'pushSender'
  | 'pushImage'
  | 'pushAction'
  | 'smsBody'
  | 'smsSender'
  | 'emailSubject'
  | 'emailBody'
  | 'emailPreheader'
  | 'emailSender'
  | 'viberText'
  | 'viberId'
  | 'viberButton'
  | 'viberImage'
  | 'wabaBody'
  | 'wabaTemplate'
  | 'wabaLang'
  | 'wabaSender'
  | 'triggerOffset'
  | 'unknownChannel';

/** Детализация «почему блок не готов» (без проверки входящего ребра — её делает вызывающий при необходимости). */
export function channelNodeDataLaunchIssues(raw: unknown, opts?: ChannelNodeReadinessOpts): ChannelNodeLaunchIssueCode[] {
  const fromSingle = String(opts?.subscriberContactListId ?? '').trim();
  const fromMany = opts?.subscriberContactListIds;
  const hasCampaignLists =
    Boolean(fromSingle) ||
    (Array.isArray(fromMany) && fromMany.some((x) => String(x ?? '').trim()));
  const nonEmpty = (s: unknown) => Boolean(String(s ?? '').trim());
  const d = normalizeChannelNodeData(raw);
  const issues: ChannelNodeLaunchIssueCode[] = [];
  const isEvent = Boolean(opts?.isEventChain);

  const pushSchedule = () => {
    if (isEvent) return;
    if (!scheduleReadyForChannel(d, opts)) issues.push('schedule');
  };

  const pushTriggerOffset = () => {
    if (!isEvent) return;
    if (d.channel !== 'sms' && d.channel !== 'viber' && d.channel !== 'email') return;
    const rawOff = String((d as { triggerOffsetDays?: string }).triggerOffsetDays ?? '').trim();
    const off = Number(rawOff);
    if (!rawOff || !Number.isFinite(off) || off < 0) issues.push('triggerOffset');
  };

  switch (d.channel) {
    case 'push':
      if (!nonEmpty(d.title)) issues.push('pushTitle');
      if (!nonEmpty(d.body)) issues.push('pushBody');
      if (!nonEmpty(d.senderName)) issues.push('pushSender');
      if (!nonEmpty(d.imageUrl)) issues.push('pushImage');
      if (!nonEmpty(d.actionUrl)) issues.push('pushAction');
      pushSchedule();
      break;
    case 'sms': {
      if (!nonEmpty(d.body)) issues.push('smsBody');
      if (!nonEmpty(d.senderName)) issues.push('smsSender');
      pushSchedule();
      pushTriggerOffset();
      if (!isEvent) {
        const listOk =
          hasCampaignLists ||
          recipientModeFilled(d.recipientMode, d.contactListQuery, d.phonesManual, null);
        if (!listOk) issues.push('recipients');
      }
      break;
    }
    case 'email': {
      if (!nonEmpty(d.title)) issues.push('emailSubject');
      if (!nonEmpty(d.body)) issues.push('emailBody');
      if (!nonEmpty(d.preheader)) issues.push('emailPreheader');
      if (!nonEmpty(d.senderName)) issues.push('emailSender');
      pushSchedule();
      pushTriggerOffset();
      if (!isEvent && !hasCampaignLists) issues.push('emailCampaignLists');
      break;
    }
    case 'viber': {
      if (!nonEmpty(d.body)) issues.push('viberText');
      if (!nonEmpty(d.viberId)) issues.push('viberId');
      pushSchedule();
      pushTriggerOffset();
      if (!isEvent) {
        const recOk =
          hasCampaignLists ||
          recipientModeFilled(d.recipientMode, d.contactListQuery, d.phonesManual, null);
        if (!recOk) issues.push('recipients');
      }
      if (d.messageType === 'withButton') {
        if (!nonEmpty(d.buttonText) || !nonEmpty(d.buttonUrl)) issues.push('viberButton');
      } else if (d.messageType === 'image') {
        if (!nonEmpty(d.imageUrl)) issues.push('viberImage');
      }
      break;
    }
    case 'waba':
      if (!nonEmpty(d.body)) issues.push('wabaBody');
      if (!nonEmpty(d.templateName)) issues.push('wabaTemplate');
      if (!nonEmpty(d.languageCode)) issues.push('wabaLang');
      if (!nonEmpty(d.senderName)) issues.push('wabaSender');
      pushSchedule();
      break;
    default:
      issues.push('unknownChannel');
  }

  return issues;
}

/** Минимальная заполненность узла канала: только «зелёный» контур, когда заполнены все поля блока под этот канал. */
export function channelNodeDataLaunchReady(raw: unknown, opts?: ChannelNodeReadinessOpts): boolean {
  return channelNodeDataLaunchIssues(raw, opts).length === 0;
}

/** Все шаги с данным каналом в порядке графа проходят проверку контента. */
export function visualListChannelReady(
  nodes: Node[],
  edges: Edge[],
  channel: MailingChannel,
  opts?: ChannelNodeReadinessOpts,
): boolean {
  const steps = linearizeVisualChannelSteps(nodes, edges).filter((s) => s.channel === channel);
  if (steps.length === 0) return false;
  for (const s of steps) {
    const n = nodes.find((x) => String(x.id) === s.nodeId);
    if (!n || !channelNodeDataLaunchReady(n.data, opts)) return false;
  }
  return true;
}

/** Каскад Viber+SMS по графу: между первым и вторым узлом канала должен быть хотя бы один «Ожидание». */
export function visualSmsViberDelayBetweenChannelsReady(
  nodes: Node[],
  edges: Edge[],
  channelsInGraphOrder: readonly MailingChannel[],
): boolean {
  if (channelsInGraphOrder.length !== 2) return true;
  const set = new Set(channelsInGraphOrder);
  if (set.size !== 2 || !set.has('sms') || !set.has('viber')) return true;
  const path = linearizeVisualPathNodes(nodes, edges);
  const chIdx: number[] = [];
  for (let i = 0; i < path.length; i++) {
    if (path[i]?.type !== 'mailingChannel') continue;
    const d = normalizeChannelNodeData(path[i].data);
    if (d.channel === 'sms' || d.channel === 'viber') chIdx.push(i);
  }
  if (chIdx.length < 2) return false;
  const between = path.slice(chIdx[0] + 1, chIdx[1]);
  return between.some((n) => n.type === 'mailingDelay');
}

export function visualNodesAllChannelsReady(
  nodes: Node[],
  edges: Edge[],
  channels: readonly MailingChannel[],
  opts?: ChannelNodeReadinessOpts,
): { ok: true } | { ok: false; failingChannel: MailingChannel } {
  for (const ch of channels) {
    if (!visualListChannelReady(nodes, edges, ch, opts)) return { ok: false, failingChannel: ch };
  }
  return { ok: true };
}
